import React, { useEffect } from 'react';
import { 
  Layers, Copy, Trash2, AlertCircle, 
  ArrowRight, FastForward, Shield, Users 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button'; // Ensuring consistent alias pathing
import { Role } from '@/types';

/**
 * 🛰️ RECURRENCE ACTION DIALOG v2.0 (Hybrid Scope Build)
 * Manages recursive data manipulation for both Personal and Shared partitions.
 */

export type RecurrenceChoice = 'instance' | 'future' | 'series';

interface RecurrenceActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: RecurrenceChoice) => void;
  mode: 'edit' | 'delete';
  eventTitle?: string;
  // 🛡️ Hybrid Scope Props
  eventScope: 'personal' | 'household';
  userRole?: Role;
}

export const RecurrenceActionDialog: React.FC<RecurrenceActionDialogProps> = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  mode,
  eventTitle = "this event",
  eventScope,
  userRole = Role.MEMBER
}) => {
  const { t } = useTranslation();
  
  // 🛡️ Permission Check: Non-admins can't perform "Global Series" deletes on shared events
  const canModifySeries = eventScope === 'personal' || userRole === Role.ADMIN || userRole === Role.OWNER;

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isDelete = mode === 'delete';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      
      <div className="bg-white dark:bg-slate-950 w-full max-w-lg rounded-[3.5rem] shadow-2xl border border-slate-100 dark:border-slate-800 p-10 space-y-8 relative overflow-hidden">
        
        {/* Scope Indicator Badge */}
        <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1 bg-slate-100 dark:bg-slate-900 rounded-full border border-slate-200 dark:border-slate-800 z-20">
          {eventScope === 'personal' ? (
            <>
              <Shield className="w-3 h-3 text-emerald-500" />
              <span className="text-[8px] font-black uppercase tracking-tighter text-slate-500">Personal Partition</span>
            </>
          ) : (
            <>
              <Users className="w-3 h-3 text-brand" />
              <span className="text-[8px] font-black uppercase tracking-tighter text-slate-500">Shared Household Hub</span>
            </>
          )}
        </div>

        {/* Visual Glow Header */}
        <div className={`absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl opacity-20 ${
          isDelete ? 'bg-rose-500' : 'bg-indigo-500'
        }`} />

        <div className="relative z-10 text-center space-y-4">
          <div className={`inline-flex p-5 rounded-[2rem] shadow-2xl ${
            isDelete 
              ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 shadow-rose-500/10' 
              : 'bg-indigo-50 dark:bg-indigo-900/20 text-brand shadow-brand/10'
          }`}>
            {isDelete ? <Trash2 className="w-10 h-10" /> : <Layers className="w-10 h-10" />}
          </div>
          
          <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter leading-none uppercase italic">
            {isDelete ? t('cal.del_loop', 'Delete Loop?') : t('cal.upd_series', 'Update Series?')}
          </h3>
          <p className="text-xs font-bold text-slate-400 max-w-[320px] mx-auto leading-relaxed">
            {isDelete 
              ? `Choose the removal scope for "${eventTitle}".`
              : `Define the update depth for this recurring sequence.`}
          </p>
        </div>

        <div className="flex flex-col gap-3 relative z-10">
          
          {/* OPTION 1: THE INSTANCE */}
          <ChoiceCard 
            icon={<Copy className="w-5 h-5" />}
            title="Just this instance"
            subtitle="Isolated change for this specific slot"
            onClick={() => onSelect('instance')}
            variant="ghost"
          />

          {/* OPTION 2: FUTURE SLICING */}
          <ChoiceCard 
            icon={<FastForward className="w-5 h-5" />}
            title="This and following"
            subtitle="Preserve past history, pivot future"
            onClick={() => onSelect('future')}
            variant="ghost"
          />

          {/* OPTION 3: THE GLOBAL SERIES */}
          <ChoiceCard 
            icon={<Layers className="w-5 h-5" />}
            title="Full series synchronization"
            subtitle="Universal sync (Historical & Future)"
            onClick={() => onSelect('series')}
            variant={isDelete ? 'danger' : 'primary'}
            disabled={!canModifySeries}
          />
          {!canModifySeries && (
            <p className="text-[8px] font-black text-rose-500 uppercase tracking-widest text-center">
              Requires Admin Privileges for Shared Series
            </p>
          )}

        </div>
        
        <div className="flex flex-col items-center gap-6 pt-4 border-t border-slate-50 dark:border-slate-800/50">
          <button 
            type="button"
            onClick={onClose} 
            className="text-2xs font-black uppercase tracking-[0.3em] text-slate-400 hover:text-brand transition-colors"
          >
            Cancel & Keep Current State
          </button>

          <div className="flex items-center gap-2 opacity-30">
             <AlertCircle className="w-3 h-3 text-slate-500" />
             <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
                {eventScope === 'personal' ? 'Privacy Protocol 1.0 Active' : 'Household Permission Guard Active'}
             </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* --- SUB-COMPONENT: CHOICE CARD --- */
interface ChoiceCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  variant: 'ghost' | 'primary' | 'danger';
  disabled?: boolean;
}

const ChoiceCard: React.FC<ChoiceCardProps> = ({ icon, title, subtitle, onClick, variant, disabled }) => {
  const styles = {
    ghost: "bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white border-2 border-transparent hover:border-brand hover:bg-indigo-50 dark:hover:bg-indigo-900/20",
    primary: "bg-brand text-white shadow-xl shadow-brand/20 hover:bg-brand-hover",
    danger: "bg-rose-600 text-white shadow-xl shadow-rose-600/20 hover:bg-rose-700"
  };

  return (
    <button 
      onClick={onClick}
      disabled={disabled}
      className={`group w-full h-20 rounded-[1.5rem] px-6 flex items-center gap-5 transition-all active:scale-95 ${styles[variant]} ${disabled ? 'opacity-30 grayscale cursor-not-allowed' : ''}`}
    >
      <div className={`p-3 rounded-2xl ${variant === 'ghost' ? 'bg-white dark:bg-slate-800 text-slate-400 group-hover:text-brand' : 'bg-white/20'}`}>
        {icon}
      </div>
      <div className="flex-1 text-left">
        <p className={`text-sm font-black tracking-tight ${variant === 'ghost' ? 'text-slate-900 dark:text-white' : 'text-white'}`}>
          {title}
        </p>
        <p className={`text-2xs font-bold uppercase tracking-widest ${variant === 'ghost' ? 'text-slate-400' : 'text-white/60'}`}>
          {subtitle}
        </p>
      </div>
      {!disabled && <ArrowRight className={`w-4 h-4 transition-all group-hover:translate-x-1 ${variant === 'ghost' ? 'text-brand opacity-0 group-hover:opacity-100' : 'text-white opacity-40 group-hover:opacity-100'}`} />}
    </button>
  );
};